import apiClient from '@/api';
import {
	CompactProcessObject,
	Ttms,
	CreateProcessObjectRequest,
	ProcessObjectSortColumns,
	ProcessObject
} from './types';
import { QueryClient } from '@tanstack/react-query';
import { Params } from '@/utils/types';
import { DEFAULT_PAGE_SIZE } from '@/routes/measurement-group/list/constants';
import { buildQueryParams } from '@/utils/build-query-params';
import { QueryMeasurementGroupsArgs } from '@/routes/measurement-group/list/types';
import { TIME_CONSTANTS_IN_MS } from '@/constants';
export const PROCESS_OBJECTS_ROOT_API_URL = '/api/v1/process-objects';
export const TTMS_BASE_API_URL = '/api/v1/ttms';

export function queryProcessObject(id: string) {
	return apiClient.get<ProcessObject>(`${PROCESS_OBJECTS_ROOT_API_URL}/${id}`);
}

function queryAllProcessObjects(params: QueryMeasurementGroupsArgs) {
	const { limit, sortDirection, sortBy, page, search } = params;

	const queryParams = buildQueryParams(params, ProcessObjectSortColumns);

	return {
		queryKey: ['process-objects', limit, sortBy, sortDirection, page, search],
		queryFn: () => apiClient.get(`${PROCESS_OBJECTS_ROOT_API_URL}?${queryParams}`)
	};
}

export function getMinifiedProcessObjects() {
	return {
		queryKey: ['process-objects', 'summary'],
		queryFn: () => {
			return apiClient.get<CompactProcessObject[]>(`${PROCESS_OBJECTS_ROOT_API_URL}/summary`);
		}
	};
}

export function queryAllTtms() {
	return {
		queryKey: ['ttms'],
		queryFn: () => {
			return apiClient.get<Ttms[]>(`${TTMS_BASE_API_URL}`);
		}
	};
}

export const getTtmsLabel = (ttms: Ttms) => {
	if (!ttms) return '';

	if (!ttms.city || !ttms.state || !ttms.ttmsZone) {
		return ttms.id.toString();
	}

	const zone = ttms.ttmsZone.split(' - ')[1];

	return `${ttms.id} - ${ttms.city}, ${ttms.state} - ${zone}`;
};

export function createProcessObject(payload: CreateProcessObjectRequest) {
	return apiClient.post<ProcessObject>(`${PROCESS_OBJECTS_ROOT_API_URL}`, payload);
}

export function processObjectListLoader(queryClient: QueryClient) {
	return async ({ request }: { request: Request }) => {
		const url = new URL(request.url);

		const page = url.searchParams.get(Params.page) || '1';
		const limit = url.searchParams.get(Params.pageSize) || DEFAULT_PAGE_SIZE;
		const sortBy = url.searchParams.get(Params.sortBy) || '';
		const sortDirection = url.searchParams.get(Params.sortDirection) || '';
		const search = url.searchParams.get(Params.search) || '';

		const queryParams = {
			page,
			limit,
			sortBy,
			sortDirection,
			search
		};

		const processObjectList = await queryClient.fetchQuery({
			...queryAllProcessObjects(queryParams),
			staleTime: TIME_CONSTANTS_IN_MS.FIVE_MIN
		});

		const ttmsList = await queryClient.ensureQueryData(queryAllTtms());

		return {
			processObjectList,
			ttmsList
		};
	};
}
